import { useState } from 'react';

import chapterImg1 from '../../../../assets/images/courses/collaboration-fundamentals/chapter5.1.jpg';
import chapterImg3 from '../../../../assets/images/courses/collaboration-fundamentals/chapter5.2.png';
// import chapterImg4 from '../../../../assets/images/courses/collaboration-fundamentals/chapter5.3.png';
import introImg from '../../../../assets/images/courses/collaboration-fundamentals/chapter5.jpg';
import {
  goalDriven,
  peopleVision,
  taskDriven,
  visionDriven,
} from '../../../../courses/collaboration-fundamentals/table-data';
import { CourseChapter } from '../../../../courses/courses.types';
import { InfoBlock } from '../../InfoBlock';
import { JigSawModule2 } from '../../JigSawModule2';
import { MediaBlock } from '../../MediaBlock';
import { TabsBlock } from '../../TabsBlock';

interface CollaborationFundamentalsProps {
  chapter: CourseChapter;
  onNextChapter: (nextIndex: number) => void;
  totalPages: number;
  courseId?: string;
  onQuizResult?: (result: boolean | undefined) => void;
}

export const CollaborationFundamentalsChapter5: React.FC<CollaborationFundamentalsProps> = ({
  chapter,
  onNextChapter,
  totalPages,
  // courseId,
}) => {
  const [engagementProfile, setEngagementProfile] = useState<number | null>(null);
  const handleNextChapterClick = () => {
    onNextChapter(chapter.index + 1);
  };
  const handlePrint = (view: string) => {
    const printArea = document.getElementById(view);
    if (printArea) {
      printArea.classList.add('print-view');

      const cleanup = () => {
        printArea.classList.remove('print-view');
        window.onafterprint = null;
      };

      window.onafterprint = cleanup;

      window.print();
    }
  };

  const boxes = [
    {
      id: '1',
      title: 'Goal Driven',
      content: goalDriven,
    },
    {
      id: '2',
      title: 'Task Driven',
      content: taskDriven,
    },
    {
      id: '3',
      title: 'Vision Driven',
      content: visionDriven,
    },
    {
      id: '4',
      title: 'People Vision',
      content: peopleVision,
    },
  ];
  const showYoureListeningTabData = [
    {
      title: 'FACTS-DRIVEN',
      htmlContent: `<p>These individuals focus on facts and achieving goals. Stick to concrete information and objectives when communicating with them. Avoid fluffy conversations and provide clear, factual updates.</p>`,
    },
    {
      title: 'TASK-DRIVEN',
      htmlContent: `<p>They prioritize action plans and details. Share specific steps and deadlines, and provide structure in discussions. Stick to agendas and timelines to keep them engaged and on track.</p>`,
    },
    {
      title: 'VISION-DRIVEN',
      htmlContent: `<p>Future-oriented and creative, they enjoy brainstorming and discussing possibilities. Encourage innovative thinking by discussing how ideas fit into the bigger picture and potential outcomes.</p>`,
    },
    {
      title: 'PEOPLE-DRIVEN',
      htmlContent: `<p>These individuals prioritize relationships and emotions. Show empathy and ask about their feelings and concerns. Give them opportunities to share their thoughts openly and feel heard.</p>`,
    },
  ];
  const engagementProfiles = [
    {
      id: '1',
      title: 'Goal Driven',
      content:
        'Analytical, focused on facts, figures, and efficiency. You thrive on data analysis and trend-spotting.',
    },
    {
      id: '2',
      title: 'Task Driven',
      content:
        'You´re punctual, love agendas,to-do lists, and meticulous planning. You ensure processes are followed and quality standards are met.',
    },
    {
      id: '3',
      title: 'Vision Driven',
      content:
        'You enjoy driving change, taking risks, and thinking outside the box. You´re future-oriented, always considering the big picture.',
    },
    {
      id: '4',
      title: 'People Vision',
      content:
        'You prioritize relationships, teamwork, and communication. Making everyone feel valued and respected is your priority.',
    },
  ];

  return (
    <>
      <div className="content-wrap lg:border-l lg:pl-8 lg:pt-8 pt-4 pb-10">
        <p className="text-sm italic  mb-7">
          Part {chapter.index} of {totalPages}
        </p>
        <h2 className="font-semibold text-3xl">Social Perceptiveness</h2>

        <MediaBlock mediaImage={introImg}></MediaBlock>
        <p className="text-xl text-center  font-light mt-5 mb-5">
          Ever wondered why you click instantly with some people but clash with others? It all boils
          down to how our brains are wired. Brain behavior preferences influence our strengths,
          communication style, and what stresses us out.
        </p>
        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">
          Collaboration is Critical for Many Jobs
        </h3>

        <div className="md:flex items-center mb-7">
          <div className="md:mr-8 max-w-full md:max-w-xs w-full mb-5 md:mb-0">
            <img src={chapterImg1} alt="" />
          </div>
          <p className="flex-1">
            When you know how you prefer to engage with others, you can start to figure out the rest
            of your team and how to engage with them based on their style.
            <br />
            <br />
            This is called “social perceptiveness”—the capacity to recognize what others expect in
            social situations. Putting into practice is what helps us build trust, foster rapport,
            and create a positive culture.
          </p>
          <br />
          <br />
        </div>
        <InfoBlock title="Note: " text="We will learn more about culture in a future module." />

        <h3 className="text-2xl text-purple-600 font-bold mt-4 mb-1">Engagement Profiles</h3>
        <p className="text-xl text-left font-light mt-5 mb-5">
          According to brain-based models, there are four engagement profiles or preferences. Let's
          break them down:
        </p>
        <p className="text-xl text-center font-bold mt-5 mb-5">
          Click on each engagement preference on the television screen below for more information
          and click the X on the top right corner to return to the main screen.
        </p>
        <div className="mb-5">
          {' '}
          <MediaBlock mediaImage={chapterImg3}>
            {engagementProfile !== null ? (
              <div className=" relative z-50  mx-auto mb-[10%]  text-left">
                <button
                  onClick={() => setEngagementProfile(null)}
                  className="text-sm font-extrabold text-red-600 absolute top-0 right-4  "
                >
                  X
                </button>

                <h1 className="text-2xl md:text-sm font-semibold mb-2">
                  {engagementProfiles[engagementProfile].title}
                </h1>
                <p className="text-xl md:text-sm leading-8">
                  {engagementProfiles[engagementProfile].content}
                </p>
              </div>
            ) : (
              <div className="mx-auto  grid grid-cols-2 md:grid-cols-2 gap-1 md:gap-2 mb-[20%] md:mb-[15%] lg:mb-[10%]">
                {engagementProfiles.map((profile, index) => (
                  <button
                    onClick={() => setEngagementProfile(index)}
                    key={index}
                    className="text-lg md:text-lg py-3 px-3 whitespace-nowrap font-semibold sm:font-normal  hover:bg-neutral-700 rounded-lg"
                  >
                    {profile.title}
                  </button>
                ))}
              </div>
            )}
          </MediaBlock>
        </div>

        <InfoBlock
          title=""
          text="Remember, we're discussing how you prefer to engage with your team, not your capabilities. You might resonate with one or two of these preferences, which explains why you connect with certain individuals who share the same preferences."
        />
        <p className="text-2xl text-center font-light mt-7 mb-7">
          Once you've identified your strengths, find ways to leverage them within your team.
          Volunteer for projects where your preference shines, or partner with someone whose
          strengths complement yours. You might not be able to change your brain, but you can
          certainly play to its strengths.
          <br />
          <br />
          Let's take a deeper dive into your engagement style with the activity below.
        </p>

        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">
          Understanding Your Engagement Style
        </h3>
        <p className="text-xl text-center font-bold mt-5 mb-5">
          Click on each style below to learn more about the different engagement styles.
        </p>

        <div className="mb-5">
          <JigSawModule2
            backgroundImage="../../../../assets/images/courses/collaboration-fundamentals/chapter5.3.png"
            boxes={boxes}
          />{' '}
        </div>

        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">Reflect</h3>
        <p className="text-xl text-center font-bold mt-5 mb-5">
          Take a moment to reflect and complete the questions below. You will be able to print your
          responses upon completion.
        </p>

        <div id="print-view1" className="mt-14">
          <ol className="list-decimal pl-7 mb-14 text-xl font-light">
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">Which of the four engagement styles feel most like you? </p>
                <textarea
                  className="w-full h-14 p-1 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  How might influence how you show up and engage with your team?(Think about how you
                  interact in meetings. how you communicate and how you respond to high-pressure
                  situations).
                </p>
                <textarea
                  className="w-full h-14 p-1 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
            <li className="mb-5">
              {' '}
              <div>
                <p className="m-4">
                  How dose knowing your own style help you when it comes to interactiong with you
                  teammates communicating, and influencing?{' '}
                </p>
                <textarea
                  className="w-full h-14 p-1 border rounded"
                  placeholder="Enter your text here..."
                ></textarea>
              </div>
            </li>
          </ol>

          <div className="mt-5 text-right">
            <button onClick={() => handlePrint('print-view1')} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">
          Engage Effectively With Team Members
        </h3>
        <p className="text-2xl text-center font-light mt-5 mb-5">
          Understanding how people prefer to engage with others can significantly improve teamwork
          and communication. By recognizing their preferred style, you can build trust, foster
          rapport, and create a positive team culture.
        </p>
        <p className="text-lg text-left font-normal mt-5 mb-5">
          Here's how to identify and engage with different engagement styles:
        </p>

        <TabsBlock tabsData={showYoureListeningTabData} />
        <h3 className="text-2xl mt-8  text-center font-light  mb-5">
          It's important not to label or categorize people but rather use these insights to connect
          better. By adjusting your communication style to align with their preferences, you can
          deepen relationships and build trust within the team.
        </h3>
        <h3 className="text-2xl text-purple-600 font-bold mt-5 mb-1">
          Activity: Social Perceptiveness
        </h3>
        <p className="text-2xl text-center font-light mt-5 mb-5">
          Reflect on a team you have participated with. It could be a team related to a: sport,
          school project, work, or even at home. Using the chart below, complete the following
          steps.
        </p>
        <br />
        <br />
        <h3 className="text-xl  font-semibold mt-5 mb-1">Step 1: Identify Your Teammates</h3>
        <p className="text-lg text-left font-normal mt-1 mb-5">
          Make a list of everyone on your team. Enter their names under the 'Name' column
        </p>

        <h3 className="text-xl  font-semibold mt-5 mb-1">
          Step 2: Identify Your Teammates’ Engagement Style
        </h3>
        <p className="text-lg text-left font-normal mt-1 mb-5">
          Identify your team members preferred engagement style based on what you have learned so
          far about engagement styles. What profiles/style you think they might have based on prior
          interactions? Enter the style for each person under the 'Preference' column.
        </p>
        <h3 className="text-xl  font-semibold mt-5 mb-1">
          Step 3: Identify Your Teammates’ Strengths & Weaknesses
        </h3>
        <p className="text-lg text-left font-normal mt-1 mb-5">
          Identify your team members' strengths and weakness. Enter in their perspective columns.
        </p>
        <h3 className="text-xl  font-semibold mt-5 mb-1">
          Step 5: Engage in a Way that’s Aligned with Team Member Preferences
        </h3>
        <p className="text-lg text-left font-normal mt-1 mb-5">
          Complete the last column by adding some strategies that you could practice when working
          with each individual.
        </p>

        <div id="print-view2" className="mt-7 text-sm mb-5 px-2 relative">
          <div className="overflow-x-auto">
            <table className="min-w-full ">
              <thead className="bg-purple-700">
                <tr>
                  {['Name', 'Preference', 'Strengths', 'Weaknesses', 'Strategies'].map(
                    (heading) => (
                      <th
                        key={heading}
                        className="text-white px-4 py-2 text-left whitespace-nowrap"
                      >
                        {heading}
                      </th>
                    ),
                  )}
                </tr>
              </thead>
              <tbody className="bg-purple-50 border-gray-800 border-1">
                {[...Array(5)].map((_, rowIdx) => (
                  <tr key={rowIdx}>
                    {[...Array(5)].map((_, colIdx) => (
                      <td key={colIdx} className="p-1 align-top">
                        <textarea className="w-full h-15 p-1 rounded  overflow-auto"></textarea>
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="mt-5 text-right">
            <button onClick={() => handlePrint('print-view2')} className="btn-internal">
              PRINT
            </button>
          </div>
        </div>
        <br />
        <br />
        <InfoBlock
          title="Remember: "
          text="When unsure of someone's style, appeal to all preferences by including concrete facts, action plans, future vision, and emotional connections in your communication. This ensures that your message resonates with everyone and demonstrates your adaptability as a team player."
        />

        <h3 className="text-2xl mt-2  text-center font-light mb-5">
          By accurately perceiving and adapting to others' preferences, you'll be valued as a
          cooperative and influential team member, enhancing teamwork and productivity. Another
          preference area to explore is Insights Discovery Color Energies.
        </h3>
        <button onClick={handleNextChapterClick} className="btn-internal w-full">
          Insights Discovery Color Energies
        </button>
      </div>
    </>
  );
};
